import React, { useState, useEffect, useRef } from "react";
import debounce from "lodash.debounce";
import { cn } from "tailwind-variants";
import UseSvg from "common/vue/components/UseSvg.react.vue";
import { MAX_QUANTITY_VALUE } from "../common/constants";
import "./QuantityInput.scss";

export default function QuantityInput({
  quantity = 0,
  index = 0,
  disabled = false,
  isMultiUpload = false,
  minQuantity = 1,
  isCustomSize = false,
  variantId = "",
  onChange,
  ...restProps
}) {
  const [quantityValue, setQuantityValue] = useState(Number(quantity));
  const updateQuantityRef = useRef();

  function maxQuantityValue() {
    return MAX_QUANTITY_VALUE;
  }

  function validateQuantity(value) {
    return Math.min(Math.max(minQuantity, value), maxQuantityValue());
  }

  // TODO(vue-migration): debounced callback captures onChange and index in closure
  useEffect(() => {
    updateQuantityRef.current = debounce(value => {
      const validValue = validateQuantity(value);
      setQuantityValue(validValue);
      onChange?.({
        value: validValue,
        index
      });
    }, 500);

    return () => {
      updateQuantityRef.current?.cancel?.();
    };
  }, []);

  useEffect(() => {
    setQuantityValue(Number(quantity));
  }, [quantity]);

  function updateQuantityFromButtons(value) {
    const validValue = validateQuantity(value);
    setQuantityValue(validValue);
    onChange?.({
      value: validValue,
      index
    });
  }

  function updateQuantity(value) {
    updateQuantityRef.current(value);
  }

  return (
    <div
      {...restProps}
      className={cn("quantity-input scope-237ef949", restProps?.className, {
        "quantity-input--multi-upload": isMultiUpload,
        "quantity-input--disabled": disabled,
        "quantity-input--custom-size": isCustomSize
      })}
    >
      <button
        className="quantity-input__button quantity-input__button--left scope-237ef949"
        disabled={disabled || Number(quantityValue) <= minQuantity}
        type="button"
        onClick={() => {
          updateQuantityFromButtons(Number(quantityValue) - 1);
        }}
        data-testid="quantity-input-button"
      >
        <UseSvg
          className="quantity-input__button-icon scope-237ef949"
          icon="minus"
          data-testid="quantity-input-button-icon"
        />
      </button>
      <input
        className={cn("quantity-input__input js-quantity-form-input scope-237ef949", {
          "quantity-input__input--grayed-out": Number(quantityValue) === 0
        })}
        value={quantityValue}
        onInput={e => {
          setQuantityValue(e.target.value);
          updateQuantity(e.target.value);
        }}
        type="number"
        data-variant-id={variantId}
        disabled={disabled}
        min={minQuantity}
        step="1"
        data-testid="quantity-input-input"
      />
      <button
        className="quantity-input__button quantity-input__button--right scope-237ef949"
        disabled={disabled || Number(quantityValue) >= maxQuantityValue()}
        type="button"
        onClick={() => {
          updateQuantityFromButtons(Number(quantityValue) + 1);
        }}
        data-testid="quantity-input-button-1"
      >
        <UseSvg
          className="quantity-input__button-icon scope-237ef949"
          icon="plus"
          data-testid="quantity-input-button-icon-1"
        />
      </button>
    </div>
  );
}
